import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { BrandMark } from "./components/BrandMark";
import { AppShell } from "./pages/AppShell";
import { CustomerPage } from "./pages/CustomerPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { KitchenPage } from "./pages/KitchenPage";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { MenuPage } from "./pages/MenuPage";
import { OrdersPage } from "./pages/OrdersPage";
import { OverviewPage } from "./pages/OverviewPage";
import { OwnerBillingPage } from "./pages/OwnerBillingPage";
import { PlatformRoot } from "./pages/PlatformConsole";
import { ProfilePage } from "./pages/ProfilePage";
import { RegisterPage } from "./pages/RegisterPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TablesPage } from "./pages/TablesPage";
import { api, type Me, type SessionResponse } from "./lib/api";

export default function App() {  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatformRoute = location.pathname.startsWith("/platform");
  const isLandingRoute = location.pathname === "/";
  const isCustomerRoute = location.pathname.startsWith("/t/");

  useEffect(() => {
    if (isPlatformRoute || isCustomerRoute) {
      setBooting(false);
      return;
    }

    let active = true;

    async function loadSession() {
      try {
        const session = await api<SessionResponse>("/api/auth/session");
        if (!active) return;

        if (session.realm === "platform") {
          navigate("/platform", { replace: true });
          return;
        }

        setMe({
          user: session.user,
          tenant: session.tenant,
          membership: session.membership,
        });
      } catch {
        if (active) setMe(null);
      } finally {
        if (active) setBooting(false);
      }
    }

    void loadSession();
    if (isLandingRoute) setBooting(false);
    return () => {
      active = false;
    };
  }, [isCustomerRoute, isLandingRoute, isPlatformRoute, navigate]);

  async function handleLogin(session: Me) {
    setMe(session);
    navigate("/app", { replace: true });
  }

  async function handleLogout() {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } finally {
      setMe(null);
      navigate("/login", { replace: true });
    }
  }

  if (booting) {
    return (
      <div className="boot-screen">
        <BrandMark size="lg" />
        <span className="boot-screen__name">Cafe System Control</span>
        <span className="boot-screen__line" />
        <span className="boot-screen__hint">Menyiapkan workspace...</span>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage me={me} />} />
      <Route path="/t/:token" element={<CustomerPage />} />

      <Route
        path="/login"
        element={
          me ? (
            <Navigate to="/app" replace />
          ) : (
            <LoginPage onLogin={(session) => void handleLogin(session)} />
          )
        }
      />

      <Route
        path="/register"
        element={
          me ? (
            <Navigate to="/app" replace />
          ) : (
            <RegisterPage onLogin={(session) => void handleLogin(session)} />
          )
        }
      />

      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      <Route
        path="/app"
        element={
          me ? (
            <AppShell me={me} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route index element={<RoleHome me={me!} />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="orders" element={<OrdersPage me={me!} />} />
        <Route path="menu" element={<MenuPage />} />
        <Route path="tables" element={<TablesPage />} />
        <Route path="kitchen" element={<KitchenPage me={me!} />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="billing" element={<OwnerBillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      <Route
        path="/platform/forgot-password"
        element={<Navigate to="/forgot-password" replace />}
      />
      <Route path="/platform/*" element={<PlatformRoot />} />

      <Route
        path="*"
        element={
          <Navigate
            to={
              location.pathname.startsWith("/platform")
                ? "/platform"
                : me
                  ? "/app"
                  : "/"
            }
            replace
          />
        }
      />
    </Routes>
  );
}

function RoleHome({ me }: { me: Me }) {
  if (me.membership.role === "kitchen") {
    return <Navigate to="/app/kitchen" replace />;
  }
  if (me.membership.role === "waiter" || me.membership.role === "cashier") {
    return <Navigate to="/app/orders" replace />;
  }
  return <OverviewPage me={me} />;
}
